import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppComponent } from './app.component';
import { NxModule } from '@nrwl/angular';
import { RouterModule } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { MomentModule } from 'angular2-moment';
import { SharedDataAccessModule } from '@ecosure-suite/shared/data-access';
import { ChartsModule } from 'ng2-charts';
import { SharedUtilAuthModule } from '@ecosure-suite/shared/util-auth';
import { StoreModule, ActionReducer, MetaReducer } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { storeFreeze } from 'ngrx-store-freeze';
import { SharedUtilFormattingModule } from '@ecosure-suite/shared/util-formatting';
import { SharedUtilLoggingModule } from '@ecosure-suite/shared/util-logging';
import { SharedUtilsNotificationsModule } from '@ecosure-suite/shared/utils-notifications';
import {
  CommonUiLayoutsModule,
  DefaultLayoutComponent,
  SimpleLayoutComponent
} from '@ecosure-suite/common/ui-layouts';
import { navItems } from './_nav';
import {
  BsDropdownModule,
  TabsModule,
  TypeaheadModule,
  AccordionModule,
  BsDatepickerModule,
  TimepickerModule
} from 'ngx-bootstrap';
import { NgSpinKitModule } from 'ng-spin-kit';
import { ToasterModule } from 'angular2-toaster';

import { NgBootstrapFormValidationModule } from 'ng-bootstrap-form-validation';
import { environment } from '../environments/environment';
import { NgxPermissionsModule, NgxPermissionsGuard } from 'ngx-permissions';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { CommonCountDownShellModule } from '@ecosure-suite/common/count-down/shell';
import {
  backOficeDashboardRoutes,
  BackOfficeDashboardShellModule
} from '@ecosure-suite/back-office/dashboard/shell';
import { dashboardItems } from './_dashboard_nav';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { BurialSocietyDocumentsConfiguration } from '@ecosure-suite/back-office/burial-societies/documents/data-access-documents';
import { ClaimsDocumentsConfiguration } from '@ecosure-suite/back-office/claims/claim-documents/data-access-claim-documents';
import { AuthGuard } from '@ecosure-suite/shared/utils-helpers';
import { SharedUtilValidatorsModule } from '@ecosure-suite/shared/util-validators';
import { CommonUtilStateManagementModule } from '@ecosure-suite/common/util-state-management';
import { AuthActionTypes } from '@ecosure-suite/back-office/auth/data-access-auth';
import {
  BackOfficeAuthFeatureShellModule,
  backOfficeAuthFeatureShellRoutes
} from '@ecosure-suite/back-office/auth/feature-shell';
import { SharedBackOfficeClaimsUtilsTransformationsModule } from '@ecosure-suite/shared/back-office/claims/utils-transformations';
import { SharedUtilModuleManagerModule } from '@ecosure-suite/shared/util-module-manager';

// for HttpClient import:
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';

// for Core import:
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { StompService, StompConfig } from '@stomp/ng2-stompjs';
import { stompConfig } from './custom-rx-stomp.config';
import { BW_CLAIM_ROLES, BW_CLAIM_ROLES_REQ, BW_ROLES } from './bw-roles';
import { DatePipe } from '@angular/common';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

export function debug(reducer: ActionReducer<any>): ActionReducer<any> {
  return function(state, action) {
    switch (action.type) {
      case AuthActionTypes.LOGOUT: {
        state = {
          routerReducer: state.routerReducer
        };
      }
    }

    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<any>[] = !environment.production
  ? [debug] // [storeFreeze, debug]
  : [];

@NgModule({
  declarations: [AppComponent],
  imports: [
    AccordionModule.forRoot(),
    BackOfficeDashboardShellModule.forRoot({
      navigations: dashboardItems
    }),
    // BackOfficeBurialSocietiesDataAccessModule,
    CommonUtilStateManagementModule.forRoot(environment.state),
    CommonCountDownShellModule.forRoot({
      idleInterval: environment.interval.idle,
      timeoutInterval: environment.interval.timeout,
      pingInterval: environment.interval.ping
    }),
    SharedBackOfficeClaimsUtilsTransformationsModule.forRoot(
      environment.claimTypes
    ),
    BrowserModule,
    BrowserAnimationsModule,
    BsDropdownModule.forRoot(),
    ChartsModule,
    CommonUiLayoutsModule.forRoot({
      navigation: navItems,
      languages: environment.languages
    }),
    BsDatepickerModule.forRoot(),
    LoadingBarHttpClientModule,
    LoadingBarModule,
    SharedDataAccessModule.forRoot({
      burial_societies_uri: `${environment.url.api}${
        environment.url.base_burial_societies
      }`,
      individuals_uri: `${environment.url.api}${
        environment.url.base_individuals
      }`,
      keycloak_uri: `${environment.url.issuer}${environment.url.base_keycloak}`,
      individuals_bulk_payments: `${environment.url.file}${
        environment.url.base_individuals_bulk_payments
      }`,
      claims_uri: `${environment.url.api}${environment.url.base_claims}`,
      workflow_uri: `${environment.url.api}${environment.url.base_workflow}`,
      education_uri: `${environment.url.api}${environment.url.base_education}`,
      file_storage_uri: `${environment.url.base_file_storage}`,
      reports_uri: `${environment.url.reports}`
    }),
    MomentModule,
    NgBootstrapFormValidationModule.forRoot(),
    NgIdleKeepaliveModule.forRoot(),
    NgSpinKitModule,
    NgxLoadingModule.forRoot({
      animationType: ngxLoadingAnimationTypes.wanderingCubes,
      backdropBackgroundColour: 'rgba(0,0,0,0.1)',
      backdropBorderRadius: '4px',
      primaryColour: '#ffffff',
      secondaryColour: '#ffffff',
      tertiaryColour: '#ffffff'
    }),
    NgxPermissionsModule.forRoot(),
    SharedUtilAuthModule.forRoot(environment.localStorageKey),
    SharedUtilsNotificationsModule,
    SweetAlert2Module.forRoot({
      buttonsStyling: false,
      customClass: {
        container: 'modal-content',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn'
      }
    }),
    NxModule.forRoot(),
    RouterModule.forRoot(
      [
        {
          path: '',
          redirectTo: 'dashboard',
          data: {
            title: 'Dashboard'
          },
          pathMatch: 'full'
        },
        {
          path: 'login',
          children: backOfficeAuthFeatureShellRoutes
        },
        {
          path: '',
          canActivate: [AuthGuard],
          component: DefaultLayoutComponent,
          children: [
            {
              path: 'dashboard',
              children: backOficeDashboardRoutes
            },
            {
              path: 'burial-societies',
              children: [
                {
                  path: '',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-societies/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietiesFeatureShellModule
                    )
                },
                {
                  path: 'contractors',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/contractors/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesContractorsFeatureShellModule
                    )
                },
                {
                  path: 'currencies',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/currencies/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesCurrenciesFeatureShellModule
                    )
                },
                {
                  path: 'dependants',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/dependants/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesDependantsFeatureShellModule
                    )
                },
                {
                  path: 'document-types',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/document-types/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesDocumentTypesFeatureShellModule
                    )
                },
                {
                  path: 'documents',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/documents/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesDocumentsFeatureShellModule
                    )
                },
                {
                  path: 'employee-identifier-types',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/employee-identifier-types/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesEmployeeIdentifierTypesFeatureShellModule
                    )
                },
                {
                  path: 'executive-types',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/executive-types/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesExecutiveTypesFeatureShellModule
                    )
                },
                {
                  path: 'executives',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/executives/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesExecutivesFeatureShellModule
                    )
                },
                {
                  path: 'burial-society-addons',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-society-addons/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietyAddonsFeatureShellModule
                    )
                },
                {
                  path: 'burial-society-statistics',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-society-statistics/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietyStatisticsFeatureShellModule
                    )
                },
                {
                  path: 'lead-generations',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/lead-generations/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesLeadGenerationsFeatureShellModule
                    )
                },
                {
                  path: 'packages',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/packages/feature-shell').then(
                      m => m.BackOfficeBurialSocietiesPackagesFeatureShellModule
                    )
                },
                {
                  path: 'policies',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/policies/feature-shell').then(
                      m => m.BackOfficeBurialSocietiesPoliciesFeatureShellModule
                    )
                },
                {
                  path: 'policy-addons',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/policy-add-ons/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesPolicyAddOnsFeatureShellModule
                    )
                },
                {
                  path: 'policy-holders',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/policy-holders/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesPolicyHoldersFeatureShellModule
                    )
                },
                {
                  path: 'policy-members',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/policy-members/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesPolicyMembersFeatureShellModule
                    )
                },
                {
                  path: 'provinces',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/provinces/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesProvincesFeatureShellModule
                    )
                },
                {
                  path: 'regions',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/regions/feature-shell').then(
                      m => m.BackOfficeBurialSocietiesRegionsFeatureShellModule
                    )
                },
                {
                  path: 'burial-society-summaries',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-society-summaries/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietySummariesFeatureShellModule
                    )
                },
                {
                  path: 'burial-society-type-descriptions',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-society-type-descriptions/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietyTypeDescriptionsFeatureShellModule
                    )
                },
                {
                  path: 'burial-society-types',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/burial-society-types/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesBurialSocietyTypesFeatureShellModule
                    )
                },
                {
                  path: 'relationships',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/relationships/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesRelationshipsFeatureShellModule
                    )
                },
                {
                  path: 'rejection-reasons',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/rejection-reasons/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesRejectionReasonsFeatureShellModule
                    )
                },
                {
                  path: 'event-details',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/event-details/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesEventDetailsFeatureShellModule
                    )
                },
                {
                  path: 'event-summaries',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/event-summaries/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesEventSummariesFeatureShellModule
                    )
                },
                {
                  path: 'parameters',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/burial-societies/parameters/feature-shell').then(
                      m =>
                        m.BackOfficeBurialSocietiesParametersFeatureShellModule
                    )
                }
              ]
            },
            //reports
            {
              path: 'reports',
              canActivate: [NgxPermissionsGuard],
              data: {
                title: 'Manage Reports',
                permissions: {
                  only: [
                    'CLAIM REPORTS',
                    'CLAIM SUPERVISOR',
                    'SUPERVISOR',
                    'MANAGEMENT'
                  ]
                }
              },
              children: [
                {
                  path: 'claim-reports',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/reports/claim-reports/feature-shell').then(
                      module =>
                         module.BackOfficeClaimReportsFeatureShellModule
                    )
                }, 
                {
                  path: '',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/reports/reports-dashboard/feature-shell').then(
                      module =>
                         module.BackOfficeReportsDashboardFeatureShellModule
                    )
                }
              ]
            },
            //individuals
            {
              path: 'individuals',
              canActivate: [NgxPermissionsGuard],
              data: {
                title: 'Manage Individual Policies',
                permissions: {
                  only: [
                    'OPERATIONS_SUPPORT',
                    'CALL_CENTRE',
                    'CUSTOMER_SERVICE_SHOP',
                    'INSURER',
                    'ECOSURE_SUPER_ADMIN',
                    'TECHNICAL_OPERATIONS',
                    'MANAGEMENT',
                    'CAPTURER',
                    'CALL CENTER & SHOP AGENTS',
                    'SALES AGENT (STAFF)',
                    'SALES AGENT (NON STAFF)',
                    'CALL CENTER ADVISOR',
                    'SHOP SUPERVISOR',
                    'ECOSURE MARKET DVPT OFFICER',
                    'ECOSURE CLAIMS OFFICER',
                    'ECOSURE OPERATIONS MANAGER',
                    'INSURER PARTNER',
                    'IT OPERATIONS SUPPORT',
                    'IT ADMINISTRATOR',
                    ...BW_ROLES
                  ]
                }
              },
              children: [
                {
                  path: 'bulk-operations',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/bulk-operations/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsBulkOperationsFeatureShellModule
                    )
                },
                {
                  path: 'bulk-payments',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/bulk-payments/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsBulkPaymentsFeatureShellModule
                    )
                },
                {
                  path: 'bulk-dependants',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/bulk-dependants/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsBulkDependantsFeatureShellModule
                    )
                },
                {
                  path: 'bulk-policy-holders',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/bulk-policy-holders/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsBulkPolicyHoldersFeatureShellModule
                    )
                },
                {
                  path: '',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/policy-holders/feature-shell').then(
                      m =>
                        m.BackOfficeIndividualsPolicyHoldersFeatureShellModule
                    )
                },
                {
                  path: 'dependants',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/dependants/feature-shell').then(
                      m => m.BackOfficeIndividualsDependantsFeatureShellModule
                    )
                },
                {
                  path: 'refunds',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/refunds/feature-shell').then(
                      m => m.BackOfficeIndividualsRefundsFeatureShellModule
                    )
                },
                {
                  path: 'packages',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/packages/feature-shell').then(
                      m => m.BackOfficeIndividualsPackagesFeatureShellModule
                    )
                },
                {
                  path: 'add-ons',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/add-ons/feature-shell').then(
                      m => m.BackOfficeIndividualsAddOnsFeatureShellModule
                    )
                },
                {
                  path: 'events',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/events/feature-shell').then(
                      m => m.BackOfficeIndividualsEventsFeatureShellModule
                    )
                },
                {
                  path: 'invoices',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/invoices/feature-shell').then(
                      m => m.BackOfficeIndividualsInvoicesFeatureShellModule
                    )
                },
                {
                  path: 'sponsors',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/sponsors/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsSponsorsFeatureShellModule
                    )
                },
                {
                  path: 'payments',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/payments/feature-shell').then(
                      m => m.BackOfficeIndividualsPaymentsFeatureShellModule
                    )
                },
                {
                  path: 'beneficiaries',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/beneficiaries/feature-shell').then(
                      m =>
                        m.BackOfficeIndividualsBeneficiariesFeatureShellModule
                    )
                },
                {
                  path: 'notifications',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/notifications/feature-shell').then(
                      m =>
                        m.BackOfficeIndividualsNotificationsFeatureShellModule
                    )
                },
                {
                  path: 'reconciliations',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/reconciliations/feature-shell').then(
                      module =>
                        module.BackOfficeIndividualsReconciliationsFeatureShellModule
                    )
                },
                {
                  path: 'relationships',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/relationships/feature-shell').then(
                      m =>
                        m.BackOfficeIndividualsRelationshipsFeatureShellModule
                    )
                },
                {
                  path: 'beneficiary-types',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/individuals/beneficiary-types/feature-shell').then(
                      m =>
                        m.BackOfficeIndividualsBeneficiaryTypesFeatureShellModule
                    )
                }
              ]
            },
            //claim requests
            {
              path: 'claim-requests',
              data: { title: `Manage Claim Requests` },
              children: [
                {
                  path: '',
                  canActivate: [NgxPermissionsGuard],
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/claims/claim-requests/feature-shell').then(
                      m => m.BackOfficeClaimsClaimRequestsFeatureShellModule
                    ),
                  data: {
                    permissions: {
                      only: [
                        `CLAIMS CAPTURER`,
                        `CLAIMS VERIFIER`,
                        `CLAIMS SUPERVISOR`,
                        ...BW_CLAIM_ROLES_REQ
                      ]
                    }
                  }
                },
             ]
            },
             //claim processing
             {
              path: 'claims',
              data: { title: `Manage Claims` },
              children: [
                {
                  path: '',
                  canActivate: [NgxPermissionsGuard],
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/claims/claim-requests/feature-shell').then(
                      m => m.BackOfficeClaimsClaimRequestsFeatureShellModule
                    ),
                  data: {
                    permissions: {
                      only: [
                        `CLAIMS CAPTURER`,
                        `CLAIMS VERIFIER`,
                        `CLAIMS SUPERVISOR`,
                        `CLAIMS APPROVER 1`,
                        `CLAIMS APPROVER 2`,
                        `CLAIMS APPROVER 3`,
                        `CLAIMS FINANCE`,
                        ...BW_CLAIM_ROLES
                      ]
                    }
                  }
                },
              ]
            },
            //commissions
            {
              path: 'commissions',
              canActivate: [NgxPermissionsGuard],
              data: {
                permissions: {
                  only: [
                    'OPERATIONS_SUPPORT',
                    'CALL_CENTRE',
                    'CUSTOMER_SERVICE_SHOP',
                    'INSURER',
                    'ECOSURE_SUPER_ADMIN',
                    'TECHNICAL_OPERATIONS',
                    'MANAGEMENT',
                    'CAPTURER',
                    'CALL CENTER & SHOP AGENTS',
                    'SALES AGENT (STAFF)',
                    'SALES AGENT (NON STAFF)',
                    'CALL CENTER ADVISOR',
                    'SHOP SUPERVISOR',
                    'ECOSURE MARKET DVPT OFFICER',
                    'ECOSURE CLAIMS OFFICER',
                    'ECOSURE OPERATIONS MANAGER',
                    'INSURER PARTNER',
                    'IT OPERATIONS SUPPORT',
                    'IT ADMINISTRATOR'
                  ]
                }
              },
              children: [
                {
                  path: '',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/commissions/my-commissions/feature-shell').then(
                      m =>
                        m.BackOfficeCommissionsMyCommissionsFeatureShellModule
                    )
                },
                {
                  path: 'my-registrations',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/commissions/my-registrations/feature-shell').then(
                      m =>
                        m.BackOfficeCommissionsMyRegistrationsFeatureShellModule
                    )
                },
                {
                  path: 'agent-commissions',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/commissions/agent-commissions/feature-shell').then(
                      m =>
                        m.BackOfficeCommissionsAgentCommissionsFeatureShellModule
                    )
                },
                {
                  path: 'agent-registrations',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/commissions/agent-registrations/feature-shell').then(
                      m =>
                        m.BackOfficeCommissionsAgentRegistrationsFeatureShellModule
                    )
                },
                {
                  path: 'commissions-report',
                  loadChildren: () =>
                    import('@ecosure-suite/back-office/commissions/commissions-reports/feature-shell').then(
                      m =>
                        m.BackOfficeCommissionsCommissionsReportsFeatureShellModule
                    )
                }
              ]
            }
          ]
        }
      ],
      { initialNavigation: 'enabled' }
    ),
    TabsModule.forRoot(),
    ToasterModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    TypeaheadModule.forRoot(),
    SharedUtilFormattingModule.forRoot({
      currencies: environment.currencies,
      statuses: environment.statuses,
      countries: environment.countries
    }),
    SharedUtilLoggingModule.forRoot(),
    SharedUtilValidatorsModule.forRoot(environment.validators),
    SharedUtilModuleManagerModule.forRoot(environment.subModules),
    StoreModule.forRoot(
      {},
      { metaReducers }
      // , { metaReducers: !environment.production ? [storeFreeze] : [] }
    ),
    EffectsModule.forRoot([]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule,
    TimepickerModule.forRoot(),
    BackOfficeAuthFeatureShellModule
  ],
  providers: [ 
    DatePipe,
    {
      provide: BurialSocietyDocumentsConfiguration,
      useValue: {
        url: environment.url.file
      }
    },
    {
      provide: ClaimsDocumentsConfiguration,
      useValue: {
        url: environment.url.file
      }
    },
    StompService,
    {
      provide: StompConfig,
      useValue: stompConfig
    }
  ],
  bootstrap: [AppComponent],
  entryComponents: [AppComponent]
})
export class AppModule {}
